import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { concat, concatMap, defer, forkJoin, from, of, takeWhile } from 'rxjs';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import {
  ApConfig,
  ApVendor,
  ClientRegisteredVendor,
  Company,
  CompanyVendor,
  ExemptVendor,
  Vendor,
  VendorRanking
} from 'src/app/classes/models';
import { ApSystemLinkingData } from 'src/app/interfaces/ap-system-linking-data';
import { CompanyServiceRanking } from 'src/app/interfaces/models/company-service-ranking';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { ViveStatuses } from 'src/app/interfaces/vive-statuses';
import { ChooseExemptVendorTypeModalComponent } from 'src/app/modals/choose-exempt-vendor-type-modal/choose-exempt-vendor-type-modal.component';
import { ConfirmArchiveVendorModalComponent } from 'src/app/modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ManageAccountingLinksModalComponent } from 'src/app/modals/manage-accounting-links-modal/manage-accounting-links-modal.component';
import { PreferredVendorRankingSelectionModalComponent } from 'src/app/modals/preferred-vendor-ranking-selection-modal/preferred-vendor-ranking-selection-modal.component';
import { ApVendorService } from 'src/app/services/ap-vendor.service';
import { AuthService } from 'src/app/services/auth.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { CompanyService } from 'src/app/services/company.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';
import { ViveApiError } from 'src/app/shared/vive-api/vive-api.error';

@Component({
  selector: 'app-client-my-vendors-admin',
  templateUrl: './client-my-vendor-admin.component.html',
  styleUrls: ['./client-my-vendor-admin.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorAdminComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private CompanyService: CompanyService,
    private ExemptVendorService: ExemptVendorService,
    private VendorService: VendorService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private route: ActivatedRoute,
    private Router: Router,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private ApVendorService: ApVendorService
  ) {
    this.CompanyService.getCompanyServiceRankings(
      this.UtilityService.stringifyObjectsInQueryObject({
        order: [['priorityLevel', 'ASC']]
      })
    ).subscribe({
      next: (CompanyServiceRankings: CompanyServiceRanking[]) => {
        if (CompanyServiceRankings) {
          this.CompanyServiceRankings = CompanyServiceRankings;
        }
        this._cdr.markForCheck();
      }
    });
  }

  Vendor: Vendor | ClientRegisteredVendor | ExemptVendor | null = null;
  CompanyVendor!: CompanyVendor;
  Company!: Company;

  canConvertClientRegisteredVendorToComplianceExempt: boolean = false;
  canConvertExemptVendorToClientRegisteredVendor: boolean = false;
  canConvertVendorToClientRegisteredVendor: boolean = false;
  canConvertVendorToComplianceExempt: boolean = false;

  VendorId: number | null = null;
  ExemptVendorId: number | null = null;
  ClientRegisteredVendorId: number | null = null;
  VendorRank: VendorRanking | null = null;
  CompanyServiceRanking: CompanyServiceRanking | null = null;
  CompanyServiceRankings?: CompanyServiceRanking[];

  isBusinessVerifiedClientRegisteredVendor: boolean = false;
  feeVouchersLeft: number = 0;
  feeWaived: boolean = false;
  apConfigs: ApConfig[] = [];
  apSystemLinkingDataArr: ApSystemLinkingData[] = [];
  isLinkableSystem: boolean = true;
  accountingSystems: {
    name: string;
    status: string;
    legalName: string;
    accountingId: string;
    autoLinkEnabled: boolean;
  }[] = [];

  CompanyVendorFormGroup = new CompanyVendorForm();

  ForceActiveDays = [
    { value: 1, label: '24 Hours' },
    { value: 7, label: '7 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' },
    { value: 120, label: '120 Days' },
    { value: 365, label: '1 Year' },
    { value: 'custom', label: 'Select Expiration Date (Custom)' }
  ];

  ForceActiveFormGroup = new FormGroup({
    days: new FormControl<number | string | null>(null, {
      validators: [Validators.required]
    }),
    customExpirationDate: new FormControl<Date | null>(null)
  });

  // Minimum date for custom expiration date picker (tomorrow)
  get minExpirationDate(): string {
    return DateTime.fromJSDate(new Date())
      .plus({ days: 1 })
      .toFormat('yyyy-MM-dd');
  }

  // Handle when Force Active days dropdown changes
  onForceActiveDaysChange() {
    const { days } = this.ForceActiveFormGroup.value;

    // Reset custom date if user selects a predefined option
    if (days !== 'custom') {
      this.ForceActiveFormGroup.patchValue({
        customExpirationDate: null
      });
    }
  }

  // VIVE Statuses used for determining if 'Allow Force Active' should be allowed for the Client/Vendor
  // relationship.
  ApprovedExemptStatuses: Set<ViveStatuses> = new Set([
    'Non-Verified',
    'Registering',
    'Awaiting Payment',
    'Registered',
    'Reviewing',
    'Suspended',
    'Inactive'
  ]);

  acceptableVendorConversionStatuses: Set<ViveStatuses> = new Set([
    'Registering',
    'Awaiting Payment'
  ]);

  get linkedAccounts(): boolean {
    if (!!this.apConfigs.find((config) => config.apSystem === 'eUnify')) {
      return true;
    }

    return this.apSystemLinkingDataArr.some((apSystem) => apSystem.ApVendor);
  }

  // If force active is set on a Client/Vendor relationship. Display the time left.
  getForceActiveTimeLeft() {
    const {
      value: { lastForceTempActiveDate }
    } = this.CompanyVendorFormGroup;

    if (lastForceTempActiveDate) {
      const { days } = DateTime.fromJSDate(
        new Date(lastForceTempActiveDate)
      ).diff(DateTime.fromJSDate(new Date()), 'days');
      return `${Math.ceil(days)} Days`;
    }

    return '0 Days';
  }

  // Determines if a Client can set 'force active' on the Client/Vendor record.
  allowForceActive() {
    const { forceTempActive, viveStatus } = this.CompanyVendor;
    return !!this.apConfigs.find((ApConfig) => {
      const { apSystem, isTwoWay } = ApConfig;
      return (
        (apSystem === 'eUnify' || (apSystem !== 'CamAcct' && isTwoWay)) &&
        (forceTempActive ||
          (viveStatus && this.ApprovedExemptStatuses.has(viveStatus)))
      );
    });
  }

  // Apply the force active data to the Client/Vendor relationship.
  setForceActiveDays() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.ForceActiveFormGroup
    );

    if (errors && errors['days']) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must select an amount of days.',
        { returnError: true }
      );
    }

    const {
      value: { forceActiveCounter: currentForceActiveCounter = 0 }
    } = this.CompanyVendorFormGroup;
    const {
      value: { days, customExpirationDate }
    } = this.ForceActiveFormGroup;

    let newLastForceTempActiveDate: Date;
    let confirmationMessage: string;

    if (days === 'custom') {
      if (!customExpirationDate) {
        throw this.ToastService.addErrorToast(
          'Error',
          'You must select a custom expiration date.',
          { returnError: true }
        );
      }

      const selectedDate = DateTime.fromJSDate(new Date(customExpirationDate));
      const now = DateTime.fromJSDate(new Date());

      if (selectedDate <= now) {
        throw this.ToastService.addErrorToast(
          'Error',
          'The expiration date must be in the future.',
          { returnError: true }
        );
      }

      newLastForceTempActiveDate = selectedDate.toJSDate();
      const daysUntilExpiration = Math.ceil(
        selectedDate.diff(now, 'days').days
      );
      confirmationMessage = `Once confirmed, this action cannot be undone until ${selectedDate.toFormat('MM/dd/yyyy')} (${daysUntilExpiration} days). If you would like to continue, please click the continue button.`;
    } else {
      const daysNumber = Number(days);
      newLastForceTempActiveDate = DateTime.fromJSDate(new Date())
        .plus({ days: daysNumber })
        .toJSDate();

      const timeLabel = daysNumber === 1 ? '24 hours' : `${daysNumber} days`;
      confirmationMessage = `Once confirmed, this action cannot be undone for ${timeLabel}. If you would like to continue, please click the continue button.`;
    }

    this.CompanyVendorFormGroup.patchValue({
      forceTempActive: true,
      forceActiveCounter: currentForceActiveCounter + 1,
      lastForceTempActiveDate: newLastForceTempActiveDate
    });

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: false, size: 'md' }
    );
    Object.assign(componentInstance, {
      displayText: confirmationMessage
    });

    const {
      value: {
        id: CompanyVendorId,
        VendorId,
        ClientRegisteredVendorId,
        CompanyId,
        forceTempActive,
        forceActiveCounter,
        lastForceTempActiveDate
      }
    } = this.CompanyVendorFormGroup;

    const CompanyVendorData = {
      id: CompanyVendorId,
      VendorId,
      ClientRegisteredVendorId,
      CompanyId,
      forceTempActive,
      forceActiveCounter,
      lastForceTempActiveDate
    };

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          this.CompanyVendorService.saveCompanyVendor(
            CompanyVendorData
          ).subscribe({
            next: (SavedCompanyVendor) => {
              const {
                forceTempActive,
                forceActiveCounter,
                lastForceTempActiveDate
              } = SavedCompanyVendor;
              Object.assign(this.CompanyVendor, {
                forceTempActive,
                forceActiveCounter,
                lastForceTempActiveDate
              });

              // Reset the form after successful submission
              this.ForceActiveFormGroup.reset({
                days: null,
                customExpirationDate: null
              });

              this.ToastService.addSuccessToast(
                'Success',
                'Successfully set force active status.'
              );
              this._cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  resetLocalVariables() {
    this.CompanyServiceRanking = null;
    this.apSystemLinkingDataArr = [];
  }

  private loadCompanyVendor() {
    this.resetLocalVariables();
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId')) || null;
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId')) || null;
    const ClientRegisteredVendorId =
      Number(paramMap.get('ClientRegisteredVendorId')) || null;
    const apConfigQuery = {
      ApVendor: {}
    };
    const companyVendorQuery = {
      attributes: [
        'id',
        'feeWaived',
        'VendorId',
        'ExemptVendorId',
        'ClientRegisteredVendorId',
        'CompanyId',
        'allowEinAutoLink',
        'viveStatus',
        'forceTempActive',
        'lastForceTempActiveDate',
        'forceActiveCounter',
        'vendorLastDatePaid'
      ],
      // An alias resolves only from `aliasedAttributes`; listed in
      // `attributes` it is dropped silently and the conversion gates below
      // treat every vendor as never paid.
      aliasedAttributes: ['lastPaymentDate']
    };
    const vendorQuery = {
      StreetAddress: {},
      MailingAddress: {},
      Contact: {},
      VendorTrade: {
        PrimaryTrade: {
          TradeCategory: {}
        }
      },
      // Scope the vendor's ranking to THIS client's company so the badge reflects
      // the current relationship (a vendor can be ranked by many clients) and the
      // include stays a bounded LEFT JOIN. options.required=false keeps vendors
      // with no ranking in this company loading. (VPDW-3721)
      VendorRanking: {
        options: { required: false },
        CompanyId: { $eq: { $col: 'CompanyVendor.CompanyId' } },
        CompanyServiceRanking: {}
      }
    };
    const companyQuery = {
      attributes: [
        'id',
        'legalName',
        'allowAutomatedBusinessVerificationClientRegisteredVendors',
        'allowClientRegisteredVendors'
      ],
      aliasedAttributes: ['feeVouchersLeft'],
      ApConfig: apConfigQuery
    };

    if (VendorId) {
      Object.assign(apConfigQuery.ApVendor, { VendorId });
      Object.assign(companyVendorQuery, {
        VendorId,
        Vendor: vendorQuery,
        Company: companyQuery
      });
    }

    if (ExemptVendorId) {
      Object.assign(apConfigQuery.ApVendor, { ExemptVendorId });
      Object.assign(companyVendorQuery, {
        ExemptVendorId,
        ExemptVendor: vendorQuery,
        Company: companyQuery
      });
    }

    if (ClientRegisteredVendorId) {
      Object.assign(apConfigQuery.ApVendor, { ClientRegisteredVendorId });
      Object.assign(companyVendorQuery, {
        ClientRegisteredVendorId,
        ClientRegisteredVendor: vendorQuery,
        Company: companyQuery
      });
    }

    this.CompanyVendorService.getCompanyVendor(
      this.UtilityService.stringifyObjectsInQueryObject(companyVendorQuery)
    ).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor Record found.',
            { returnError: true }
          );
        }

        this.CompanyVendor = CompanyVendor;

        this.CompanyVendorFormGroup.patchValue(this.CompanyVendor);

        const {
          Vendor,
          ExemptVendor,
          ClientRegisteredVendor,
          feeWaived,
          Company,
          viveStatus,
          lastPaymentDate,
          vendorLastDatePaid
        } = this.CompanyVendor;

        if (!Company) {
          throw this.ToastService.addErrorToast('Error', 'No Company Found.', {
            returnError: true
          });
        }

        const {
          feeVouchersLeft,
          ApConfigs,
          allowAutomatedBusinessVerificationClientRegisteredVendors,
          allowClientRegisteredVendors
        } = Company;

        this.Company = Company;

        if (Vendor) {
          this.Vendor = Vendor;
          this.VendorId = Vendor.id!;
        }

        if (ExemptVendor) {
          this.Vendor = ExemptVendor;
          this.ExemptVendorId = ExemptVendor.id!;

          forkJoin([
            this.ExemptVendorService.checkForDuplicateTaxId(ExemptVendor),
            this.ExemptVendorService.checkForDuplicateLegalName(ExemptVendor)
          ]).subscribe({
            next: ([duplicateTaxIds, duplicateLegalNames]) => {
              if (
                duplicateTaxIds &&
                duplicateTaxIds.length === 0 &&
                duplicateLegalNames &&
                duplicateLegalNames.length === 0
              ) {
                this.canConvertExemptVendorToClientRegisteredVendor = true;
              }
              this._cdr.markForCheck();
            }
          });
        }

        if (ClientRegisteredVendor) {
          this.Vendor = ClientRegisteredVendor;
          this.ClientRegisteredVendorId = ClientRegisteredVendor.id!;

          this.ClientRegisteredVendorService.checkForDuplicateTaxId(
            ClientRegisteredVendor
          ).subscribe({
            next: (duplicates) => {
              if (duplicates && duplicates.length === 0) {
                this.canConvertClientRegisteredVendorToComplianceExempt = true;
              }
              this._cdr.markForCheck();
            }
          });
        }

        if (Vendor) {
          this.Vendor = Vendor;
          this.VendorId = Vendor.id!;

          if (!viveStatus) {
            throw this.ToastService.addErrorToast(
              'Error',
              'No Vendor Status Found.',
              {
                returnError: true
              }
            );
          }

          this.canConvertVendorToClientRegisteredVendor =
            !!allowClientRegisteredVendors &&
            this.acceptableVendorConversionStatuses.has(viveStatus) &&
            !lastPaymentDate;

          // VPDW-3665: clients may convert a Full Vendor to Compliance Exempt
          // when there is no payment and no payment history. Status is not
          // gated (Inactive is allowed). Paid vendors stay staff-only.
          this.canConvertVendorToComplianceExempt =
            !lastPaymentDate && !vendorLastDatePaid;
        }

        if (!this.Vendor) {
          throw this.ToastService.addErrorToast('Error', 'No Vendor Found.', {
            returnError: true
          });
        }

        const { VendorRankings } = this.Vendor;

        if (VendorRankings && VendorRankings.length > 0) {
          const VendorRanking = VendorRankings[0];

          this.VendorRank = VendorRanking;

          if (VendorRanking) {
            const { CompanyServiceRanking } = VendorRanking;

            if (CompanyServiceRanking) {
              this.CompanyServiceRanking = CompanyServiceRanking;
            }
          }
        }

        if (feeVouchersLeft) {
          this.feeVouchersLeft = feeVouchersLeft;
        }

        if (feeWaived) {
          this.feeWaived = feeWaived;
        }

        if (allowAutomatedBusinessVerificationClientRegisteredVendors) {
          this.isBusinessVerifiedClientRegisteredVendor =
            allowAutomatedBusinessVerificationClientRegisteredVendors;
        }

        // Reset apSystemLinkingDataArr when loadCompanyVendor gets called
        this.apSystemLinkingDataArr = [];
        if (ApConfigs && ApConfigs.length > 0) {
          this.apConfigs = ApConfigs;

          ApConfigs.forEach((config) => {
            if (this.apConfigs?.length === 1 && config.apSystem === 'eUnify') {
              this.isLinkableSystem = false;
            }
            this.apSystemLinkingDataArr.push({
              config,
              ApVendor:
                config.ApVendors && config.ApVendors?.length > 0
                  ? config.ApVendors[0]
                  : null,
              CompanyVendor
            });
          });
        }
        this._cdr.markForCheck();
      }
    });
  }

  // VPDW-5560: the v3 voucher route refuses a vendor-and-sites manager, as the
  // legacy tab never offered it, so the button is not shown to one.
  get canManageFeeVouchers(): boolean {
    const role: UserAccountRoles | null | undefined =
      this.AuthService.getUser().UserAccountRole?.role;
    return role !== 'CLIENT_VENDOR_AND_SITES_MANAGER';
  }

  // VPDW-5560 (CBRI-2674): through the v3 route, which moves the relationship's
  // status itself. The legacy save ran a change action that pushed
  // relationships registered before v3 back into the funnel. The server stamps
  // the dates, so the body carries only the flag.
  addRemoveFeeWaiver() {
    const { CompanyId, VendorId } = this.CompanyVendor;

    if (!CompanyId || !VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update the fee voucher. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const feeWaived = !this.feeWaived;

    from(
      companyVendorsService.updateRelationship({
        pathParams: { companyId: CompanyId, vendorId: VendorId },
        options: {
          context: { ManuallyHandleError: true },
          requestTimeout: LOAD_BALANCER_TIMEOUT_MS
        },
        data: { feeWaived }
      })
    ).subscribe({
      next: () => {
        this.feeWaived = feeWaived;
        this.ToastService.addSuccessToast(
          'Success',
          `Successfully ${feeWaived ? 'waived' : 'un-waived'} vendor's fee.`
        );
        this.loadCompanyVendor();
        this._cdr.markForCheck();
      },
      error: (error: unknown) => {
        // A refusal carries the API's reason, such as a client that pays its
        // vendors' fees. A 5xx body is raw exception text, never shown.
        const { message } =
          error instanceof ViveApiError &&
          error.httpStatusCode !== undefined &&
          error.httpStatusCode < 500
            ? ((error.cause ?? {}) as { message?: unknown })
            : {};
        this.ToastService.addErrorToast(
          'Error',
          typeof message === 'string' && message.trim()
            ? message
            : 'The fee voucher could not be updated. Please try again.'
        );
        this._cdr.markForCheck();
      }
    });
  }

  manageAccountingLinks(apSystemLinkingData: ApSystemLinkingData) {
    const modalRef = this.ModalService.open(
      ManageAccountingLinksModalComponent,
      { windowClass: 'modal-width_larger', centered: true, size: 'xl' }
    );
    const { componentInstance } = modalRef;
    Object.assign(componentInstance, {
      Vendor: this.Vendor,
      CompanyVendor: this.CompanyVendor,
      apSystemLinkingData,
      reloadParentData: () => this.loadCompanyVendor()
    });
  }

  assignVendorRanking() {
    if (!this.VendorRank) {
      this.VendorRank = new VendorRanking({ CompanyId: this.Company.id });
    }

    const vendorRankingSelectionModal = this.ModalService.open(
      PreferredVendorRankingSelectionModalComponent,
      { windowClass: 'static', centered: true, size: 'lg' }
    );
    Object.assign(vendorRankingSelectionModal.componentInstance, {
      CompanyServiceRankings: this.CompanyServiceRankings,
      VendorRank: this.VendorRank,
      Vendor: this.Vendor
    });

    vendorRankingSelectionModal.closed.subscribe({
      next: (CompanyServiceRankingId: number | null) => {
        if (!this.VendorRank) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot save vendor ranking. No vendor ranking found.',
            { returnError: true }
          );
        }

        Object.assign(this.VendorRank, {
          CompanyServiceRankingId: CompanyServiceRankingId
        });

        if (this.VendorId) {
          Object.assign(this.VendorRank, { VendorId: this.VendorId });

          this.CompanyService.saveVendorRank(
            this.VendorId,
            this.VendorRank
          ).subscribe({
            next: () => {
              this.loadCompanyVendor();
              this._cdr.markForCheck();
            },
            error: (err) => {
              throw this.ToastService.addErrorToast('Error', err.message, {
                returnError: true
              });
            }
          });
        }

        if (this.ExemptVendorId) {
          Object.assign(this.VendorRank, {
            ExemptVendorId: this.ExemptVendorId
          });

          this.ExemptVendorService.saveVendorRank(
            this.ExemptVendorId,
            this.VendorRank
          ).subscribe({
            next: () => {
              this.loadCompanyVendor();
              this._cdr.markForCheck();
            },
            error: (err) => {
              throw this.ToastService.addErrorToast('Error', err.message, {
                returnError: true
              });
            }
          });
        }
      }
    });
  }

  convertVendorToComplianceExempt() {
    if (this.VendorId && !this.canConvertVendorToComplianceExempt) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Vendor is not convertible.',
        { returnError: true }
      );
    }

    const observableArr = [];

    observableArr.push(
      defer(() => {
        const confirmModalRef = this.ModalService.open(ConfirmModalComponent, {
          windowClass: 'static',
          centered: true,
          size: 'md'
        });
        confirmModalRef.componentInstance.displayText =
          'Are you sure you want to convert vendor to compliance exempt?';

        return confirmModalRef.closed;
      })
    );

    observableArr.push(
      defer(() => {
        const chooseExemptVendorTypeModal = this.ModalService.open(
          ChooseExemptVendorTypeModalComponent,
          { backdrop: 'static', centered: true, size: 'md' }
        );
        chooseExemptVendorTypeModal.componentInstance.CompanyId =
          this.CompanyVendor.CompanyId;

        return chooseExemptVendorTypeModal.closed.pipe(
          concatMap((result: boolean | number) => {
            if (!result) {
              return of(false);
            }

            const ExemptVendorTypeId = Number(result);

            if (!this.Vendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No Vendor found to convert',
                { returnError: true }
              );
            }

            if (!this.Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing client information.',
                { returnError: true }
              );
            }

            const { id: CompanyId } = this.Company;

            if (!ExemptVendorTypeId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Cannot Convert. No Exempt Vendor Type ID.',
                { returnError: true }
              );
            }

            const navigateToExemptVendorProfile = (
              exemptVendor: ExemptVendor
            ) => {
              if (!exemptVendor) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'No Exempt Vendor Returned.',
                  { returnError: true }
                );
              }

              const { id: ExemptVendorId } = exemptVendor;

              this.Router.navigateByUrl(
                `client/${this.AuthService.getUserCompanyId()}/vendors/my-vendor/exempt-vendor/${ExemptVendorId}/info`
              );
            };

            if (this.VendorId) {
              this.VendorService.convertVendorToComplianceExempt({
                CompanyId,
                VendorId: this.VendorId,
                ExemptVendorTypeId
              }).subscribe({
                next: (exemptVendor: ExemptVendor) => {
                  navigateToExemptVendorProfile(exemptVendor);
                },
                error: (err) => {
                  throw this.ToastService.addErrorToast('Error', err.message, {
                    returnError: true
                  });
                }
              });
            } else if (this.ClientRegisteredVendorId) {
              const { id: ClientRegisteredVendorId } = this.Vendor;

              if (!ClientRegisteredVendorId) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'No Vendor ID found while attempting to convert.',
                  { returnError: true }
                );
              }

              this.ClientRegisteredVendorService.convertClientRegisteredVendorToComplianceExempt(
                {
                  ClientRegisteredVendorId,
                  CompanyId,
                  ExemptVendorTypeId
                }
              ).subscribe({
                next: (exemptVendor: ExemptVendor) => {
                  navigateToExemptVendorProfile(exemptVendor);
                },
                error: (err) => {
                  throw this.ToastService.addErrorToast('Error', err.message, {
                    returnError: true
                  });
                }
              });
            } else {
              throw this.ToastService.addErrorToast(
                'Error',
                'No Vendor ID found while attempting to convert.',
                { returnError: true }
              );
            }

            return of(true);
          })
        );
      })
    );

    concat(...observableArr)
      .pipe(takeWhile((result) => result !== false))
      .subscribe();
  }

  convertExemptVendorToClientRegistered() {
    if (!this.ExemptVendorId || !this.Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor information.',
        { returnError: true }
      );
    }

    const { Contacts } = this.Vendor;
    const bvOrNonVerified = this.isBusinessVerifiedClientRegisteredVendor
      ? 'Business Verified'
      : 'Non-Verified';
    const observableArr = [];

    observableArr.push(
      defer(() => {
        const modalRef = this.ModalService.open(ConfirmModalComponent, {
          centered: true,
          size: 'lg'
        });
        modalRef.componentInstance.displayText = `Are you sure you want to convert this vendor from Compliance Exempt to ${bvOrNonVerified}?`;

        return modalRef.closed;
      })
    );

    if (
      !Contacts ||
      Contacts.length === 0 ||
      !Contacts.find((contact) => contact.isPrimaryCompliance && contact.email)
    ) {
      observableArr.push(
        defer(() => {
          const modalRef = this.ModalService.open(ConfirmModalComponent, {
            centered: true,
            size: 'lg'
          });
          modalRef.componentInstance.displayText =
            'You are converting vendor with no Contacts. No invitation e-mail will be sent to vendor without a contact with e-mail information. Do you want to continue?';

          return modalRef.closed;
        })
      );
    }

    observableArr.push(
      defer(() => {
        if (!this.Vendor) {
          throw this.ToastService.addErrorToast('Error', 'No Vendor Found.', {
            returnError: true
          });
        }

        if (!this.Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing client information.',
            { returnError: true }
          );
        }

        const { id: CompanyId } = this.Company;

        return this.ExemptVendorService.convertVendorToClientRegisteredVendor({
          ExemptVendorId: this.ExemptVendorId,
          CompanyId
        }).pipe(
          concatMap((ClientRegisteredVendor: ClientRegisteredVendor) => {
            if (!ClientRegisteredVendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'No ClientRegistered Vendor Found.',
                { returnError: true }
              );
            }

            const { id: ClientRegisteredVendorId } = ClientRegisteredVendor;

            this.Router.navigateByUrl(
              `client/${this.AuthService.getUserCompanyId()}/vendors/my-vendor/client-registered-vendor/${ClientRegisteredVendorId}/info`
            );
            return of(true);
          })
        );
      })
    );

    concat(...observableArr)
      .pipe(takeWhile((result) => result === true))
      .subscribe();
  }

  convertVendorToClientRegistered() {
    if (!this.VendorId || !this.Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing vendor or company information.',
        { returnError: true }
      );
    }

    const { closed, componentInstance } = this.ModalService.open(
      ConfirmModalComponent,
      {
        centered: true,
        size: 'lg',
        backdrop: 'static'
      }
    );

    Object.assign(componentInstance, {
      displayText: `Are you sure you want to convert this vendor?`
    });

    closed.subscribe((confirm) => {
      if (confirm) {
        this.ClientRegisteredVendorService.attemptToConvertViveVendorToClientRegisteredVendor(
          this.Vendor!,
          {
            VendorId: this.VendorId,
            CompanyId: this.Company.id
          }
        ).subscribe({
          next: (ClientRegisteredVendor: ClientRegisteredVendor) => {
            this.Router.navigateByUrl(
              `client/${this.AuthService.getUserCompanyId()}/vendors/my-vendor/client-registered-vendor/${ClientRegisteredVendor.id}/info`
            );
          }
        });
      }
    });
  }

  get canArchiveClientCreatedRelationship(): boolean {
    return (
      !!this.CompanyVendor &&
      !this.CompanyVendor.isArchived &&
      !this.CompanyVendor.VendorId &&
      (this.CompanyVendor.viveStatus === 'Non-Verified' ||
        this.CompanyVendor.viveStatus === 'Compliance Exempt')
    );
  }

  archiveClientCreatedRelationship() {
    const companyVendorId = this.CompanyVendor?.id;

    if (!companyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot archive vendor. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmArchiveVendorModalComponent,
      { backdrop: 'static', size: 'lg' }
    );
    Object.assign(componentInstance, {
      modelType: 'companyVendor',
      noteTitle: 'Archived by client',
      subtitle: 'This removes the vendor from your list.',
      notePlaceholder: 'Why are you archiving this vendor?'
    });

    from(result).subscribe({
      next: (archiveNote) => {
        if (!archiveNote) {
          return;
        }

        from(
          companyVendorsService.archiveClientCreatedRelationship({
            pathParams: { companyVendorId },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
            data: { note: archiveNote.note }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success!',
              'Successfully archived vendor.'
            );

            this.Router.navigateByUrl(
              `client/${this.AuthService.getUserCompanyId()}/vendors/client`
            );
          }
        });
      }
    });
  }

  syncNow(apVendor?: ApVendor | null) {
    if (!apVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No AP Vendor found to sync.',
        { returnError: true }
      );
    }

    const { id } = apVendor;

    this.ApVendorService.saveApVendor({
      id,
      wasHoldSet: true,
      wasStatusSet: true,
      syncAt: new Date(),
      lastTimeOnHoldChecked: null,
      nextTimeActiveStatusChecked: null
    }).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully set AP Vendor to sync now.'
        );
        this._cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.loadCompanyVendor();
  }
}
